import { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { fetchGroupsFlat, UserGroupFlat } from '../features/userGroups/userGroupsApi';

interface Props {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  ownerUserId?: number;
  loading?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

function labelForFlat(groups: UserGroupFlat[], id: string): string {
  const g = groups.find((x) => x.id === id);
  return g?.name || id;
}

export function UserGroupMultiSelect({
  selectedIds,
  onChange,
  ownerUserId,
  loading: externalLoading,
  label = 'Grupos',
  placeholder = 'Selecciona grupos (solo los tuyos)',
  disabled = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [groups, setGroups] = useState<UserGroupFlat[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchGroupsFlat(ownerUserId);
        if (!cancelled) setGroups(data);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerUserId]);

  const busy = externalLoading || loading;

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground flex items-center justify-between"
          disabled={disabled || busy}
        >
          <span className="truncate">
            {selectedIds.length === 0 ? placeholder : `${selectedIds.length} grupo(s)`}
          </span>
          <Icon
            icon={isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
            className="w-4 h-4 text-muted-foreground ml-2 flex-shrink-0"
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-auto">
            {busy && (
              <div className="p-3 text-sm text-muted-foreground text-center">Cargando grupos...</div>
            )}
            {!busy && groups.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground text-center">
                Crea grupos en «Mis grupos» para compartir por equipo
              </div>
            )}
            {!busy &&
              groups.map((g) => {
                const isSelected = selectedIds.includes(g.id);
                return (
                  <label
                    key={g.id}
                    className="flex items-center px-3 py-2 hover:bg-muted cursor-pointer rounded-sm"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(g.id)}
                      className="mr-2 rounded border-input text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">{g.name}</span>
                  </label>
                );
              })}
          </div>
        )}
      </div>
      {selectedIds.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
            >
              {labelForFlat(groups, id)}
              <button
                type="button"
                onClick={() => toggle(id)}
                className="ml-1 hover:text-primary/80"
              >
                <Icon icon="mdi:close" className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-1">
        Solo aparecen grupos del dueño del formulario. Los miembros del grupo heredan el acceso indicado.
      </p>
    </div>
  );
}
